import Button from 'components/form/Button';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { commonRoute, icons } from 'utils/constants';
import { checkEmailVerification, getDataFromLocalStorage } from 'utils/helpers';
import PreviewEducationDetailsPopup from '../ProfileInfo/PreviewEducationDetailsPopup';
import {
  fetchResearchProfileData,
  setIsGlobalProfilePreviewPopup,
} from 'store/slices';
import PreviewBasicInfoPopup from '../ProfileInfo/PreviewBasicInfoPopup';
import PreviewProfessionalDetailsPopup from '../ProfileInfo/PreviewProfessionalDetailsPopup';
import { useNavigate } from 'react-router-dom';
import GlobalProfilePreviewPopup from 'components/Layout/GlobalProfilePreviewPopup';

const ProfileCompletion = ({ researchProfileCount, isShowButton }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const remainPercentage = 100 - researchProfileCount;
  const [activeModal, setActiveModal] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);

  let { user_type, registration_email_otp_status, user_updation_status, id } =
    getDataFromLocalStorage();

  const { isGlobalProfilePreviewPopup } = useSelector((state) => ({
    isGlobalProfilePreviewPopup: state.global.isGlobalProfilePreviewPopup,
  }));

  const fetchDetails = async () => {
    setIsPageLoading(true);
    await dispatch(fetchResearchProfileData(`user_id=${id}`));
    setIsPageLoading(false);
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderModal = () => {
    switch (activeModal) {
      case 'verify_email':
        navigate(commonRoute.verifyEmail);
        return null; // prevent fallthrough loop
      case 'basic_detail':
        return <PreviewBasicInfoPopup onHide={() => setModalShow(false)} />;
      case 'education_detail':
        return (
          <PreviewEducationDetailsPopup onHide={() => setModalShow(false)} />
        );
      case 'professional_detail':
        return (
          <PreviewProfessionalDetailsPopup onHide={() => setModalShow(false)} />
        );
      case 'certificate_detail':
        return (
          <PreviewEducationDetailsPopup
            onHide={() => setModalShow(false)}
            isCertificate
          />
        );
      default:
        return null;
    }
  };

  const {
    email_verification_status,
    basic_details_status,
    education_details_status,
    professional_details_status,
    certificate_details_status,
  } = user_updation_status || {};

  // ✅ Memoize sections to avoid re-creation each render
  const sections = useMemo(
    () => [
      {
        label: 'Verify Email ID',
        completed: email_verification_status === '1',
        action: email_verification_status === '1' ? 'Edit Details' : 'Add Details',
        key: 'verify_email',
      },
      {
        label: 'Basic Detail',
        completed: basic_details_status === '1',
        action: basic_details_status === '1' ? 'Edit Details' : 'Add Details',
        key: 'basic_detail',
      },
      {
        label: 'Education Details',
        completed: education_details_status === '1',
        action: education_details_status === '1' ? 'Edit Details' : 'Add Details',
        key: 'education_detail',
      },
      {
        label: 'Professional Details',
        completed: professional_details_status === '1',
        action:
          professional_details_status === '1' ? 'Edit Details' : 'Add Details',
        key: 'professional_detail',
      },
      {
        label: 'Certificate Details',
        completed: certificate_details_status === '1',
        action: certificate_details_status === '1' ? 'Edit Details' : 'Add Details',
        key: 'certificate_detail',
      },
    ],
    [
      email_verification_status,
      basic_details_status,
      education_details_status,
      professional_details_status,
      certificate_details_status,
    ]
  );

  const handleEditInfo = (sectionKey) => {
    setActiveModal(sectionKey);
    setModalShow(true);
  };

  const [isAllCompleted, setIsAllCompleted] = useState(false);

  const handleProceedToComplete = () => {
    const firstIncompleteSection = sections.find(
      (section) =>
        !section.completed &&
        (section.key !== 'professional_detail' || user_type === '2')
    );

    if (firstIncompleteSection) {
      setActiveModal(firstIncompleteSection.key);
      setModalShow(true);
    } else {
      setIsAllCompleted(true);
    }
  };

  // ✅ Only update state if value actually changes
  useEffect(() => {
    const allCompleted = sections.every(
      (section) =>
        section.completed ||
        (section.key === 'professional_detail' && user_type !== '2')
    );
    if (allCompleted !== isAllCompleted) {
      setIsAllCompleted(allCompleted);
    }
  }, [sections, user_type, isAllCompleted]);

  return (
    <>
      {['2', '5']?.includes(user_type) && researchProfileCount && (
        <div
          className={`${
            registration_email_otp_status === '0' ? '' : 'cmb-24'
          } d-flex justify-content-between cmb-11 gap-3`}
        >
          <div className="d-flex justify-content-between flex-column flex-grow-1 pt-1">
            <div
              className={`d-flex justify-content-between ${
                researchProfileCount < 100 ? '' : 'mb-2'
              }`}
            >
              <div className="text-14-400-20 color-title-navy">
                Profile Status
              </div>
              <div className="text-14-400-20 color-title-navy">
                {researchProfileCount >= 100 ? 100 : researchProfileCount}%
              </div>
            </div>
            <div className="progress" style={{ height: '6px' }}>
              <div
                className="progress-bar bg-lime-green"
                role="progressbar"
                style={{ width: `${researchProfileCount}%` }}
                aria-valuenow={researchProfileCount}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>

          {registration_email_otp_status === '0' ? (
            <Button
              isSquare
              text="Verify Email ID"
              btnStyle="primary-dark"
              className="cps-20 cpe-20 rounded h-35"
              onClick={() => {
                if (!checkEmailVerification(dispatch, navigate)) return;
              }}
            />
          ) : (
            <div
              className="d-flex align-items-center gap-2 text-12-500-15"
              style={{
                border: '1px solid #2148C0',
                borderRadius: '6px',
                padding: '8px 13px',
                background: '#EFF3FF',
                color: '#2148C0',
              }}
            >
              <img src={icons?.RoundRightIcon} alt="round" />
              Verified
            </div>
          )}
        </div>
      )}

      <div className="text-14-400-18 color-black-olive cmb-24">
        Just {remainPercentage}% more to unlock all benefits and{' '}
        <span
          className="text-14-500-18 color-7cff pointer"
          onClick={() => {
            dispatch(setIsGlobalProfilePreviewPopup(true));
          }}
        >
          Update Profile
        </span>
      </div>

      <ul className="list-group list-group-flush cmb-22">
        {sections
          .filter(
            (section) =>
              section.key !== 'professional_detail' || user_type === '2'
          )
          .map(({ key, label, completed, action }, idx) => (
            <li
              key={key || idx}
              className="list-group-item d-flex align-items-center border-0 px-0 py-2"
            >
              <div className="d-flex align-items-center justify-content-center gap-2">
                <img
                  src={completed ? icons?.abstractDone : icons?.ClockPending}
                  alt={completed ? 'Completed' : 'Pending'}
                  height={24}
                  width={24}
                />
                <p className="mb-0 text-14-400-18">{label}</p>
              </div>

              {!(
                key === 'verify_email' && email_verification_status === '1'
              ) && (
                <button
                  onClick={() => handleEditInfo(key)}
                  className="btn text-decoration-none text-12-500-15 color-7cff shadow-none outline-0 border-0"
                >
                  {action === 'Edit Details' && (
                    <span className="me-1">
                      <img src={icons.primarylightEdit} alt="" />
                    </span>
                  )}
                  {action}
                </button>
              )}
            </li>
          ))}
      </ul>

      {isShowButton && (
        <div
          className="d-flex gap-3 cmb-50"
          style={{ maxWidth: '400px', width: '100%' }}
        >
          {!isAllCompleted && (
            <div className="flex-fill">
              <Button
                text="Proceed to Complete"
                btnStyle="primary-dark"
                className="h-35 cps-20 cpe-20"
                onClick={handleProceedToComplete}
              />
            </div>
          )}
          <div className="flex-fill">
            <Button
              text="Back"
              btnStyle="primary-light-outline"
              className="h-35 cps-20 cpe-20"
              onClick={() => navigate(-1)}
            />
          </div>
        </div>
      )}

      {modalShow && renderModal()}

      {isGlobalProfilePreviewPopup && (
        <GlobalProfilePreviewPopup
          isLoading={isPageLoading}
          fetchDetails={fetchDetails}
        />
      )}
    </>
  );
};

export default ProfileCompletion;


// import Button from 'components/form/Button';
// import React, { useEffect, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { commonRoute, icons } from 'utils/constants';
// import { checkEmailVerification, getDataFromLocalStorage } from 'utils/helpers';
// import PreviewEducationDetailsPopup from '../ProfileInfo/PreviewEducationDetailsPopup';
// import {
//   fetchResearchProfileData,
//   setIsGlobalProfilePreviewPopup,
// } from 'store/slices';
// import PreviewBasicInfoPopup from '../ProfileInfo/PreviewBasicInfoPopup';
// import PreviewProfessionalDetailsPopup from '../ProfileInfo/PreviewProfessionalDetailsPopup';
// import { useNavigate } from 'react-router-dom';
// import GlobalProfilePreviewPopup from 'components/Layout/GlobalProfilePreviewPopup';

// const ProfileCompletion = ({ researchProfileCount, isShowButton }) => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const remainPercentage = 100 - researchProfileCount;
//   const [activeModal, setActiveModal] = useState(null);
//   const [modalShow, setModalShow] = useState(false);
//   const [isPageLoading, setIsPageLoading] = useState(false);

//   let { user_type, registration_email_otp_status, user_updation_status, id } =
//     getDataFromLocalStorage();
//   const { isGlobalProfilePreviewPopup } = useSelector((state) => ({
//     isGlobalProfilePreviewPopup: state.global.isGlobalProfilePreviewPopup,
//   }));

//   const fetchDetails = async () => {
//     setIsPageLoading(true);
//     await dispatch(fetchResearchProfileData(`user_id=${id}`));
//     setIsPageLoading(false);
//   };

//   useEffect(() => {
//     fetchDetails();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const renderModal = () => {
//     // console.log(`Rendering modal for: ${activeModal}`); // Log the active modal
//     switch (activeModal) {
//       case 'verify_email':
//         navigate(commonRoute.verifyEmail);
//       case 'basic_detail':
//         return <PreviewBasicInfoPopup onHide={() => setModalShow(false)} />;
//       case 'education_detail':
//         return (
//           <PreviewEducationDetailsPopup onHide={() => setModalShow(false)} />
//         );
//       case 'professional_detail':
//         return (
//           <PreviewProfessionalDetailsPopup onHide={() => setModalShow(false)} />
//         );
//       case 'certificate_detail':
//         return (
//           <PreviewEducationDetailsPopup
//             onHide={() => setModalShow(false)}
//             isCertificate
//           />
//         );
//       default:
//         return null;
//     }
//   };

//   const {
//     email_verification_status,
//     basic_details_status,
//     education_details_status,
//     professional_details_status,
//     certificate_details_status,
//   } = user_updation_status || {};

//   const sections = [
//     {
//       label: 'Verify Email ID',
//       completed: email_verification_status === '1' ? true : false,
//       action: email_verification_status === '1' ? 'Edit Details' : 'Update',
//       key: 'verify_email',
//     },
//     {
//       label: 'Basic Detail',
//       completed: basic_details_status === '1' ? true : false,
//       action: basic_details_status === '1' ? 'Edit Details' : 'Update',
//       key: 'basic_detail',
//     },
//     {
//       label: 'Education Details',
//       completed: education_details_status === '1' ? true : false,
//       action: education_details_status === '1' ? 'Edit Details' : 'Update',
//       key: 'education_detail',
//     },

//     {
//       label: 'Professional Details',
//       completed: professional_details_status === '1' ? true : false,
//       action: professional_details_status === '1' ? 'Edit Details' : 'Update',
//       key: 'professional_detail',
//     },
//     {
//       label: 'Certificate Details',
//       completed: certificate_details_status === '1' ? true : false,
//       action: certificate_details_status === '1' ? 'Edit Details' : 'Update',
//       key: 'certificate_detail',
//     },
//   ];

//   const handleEditInfo = (sectionKey) => {
//     // console.log(`Edit info clicked for: ${sectionKey}`);
//     setActiveModal(sectionKey);
//     setModalShow(true);
//   };

//   const [isAllCompleted, setIsAllCompleted] = useState(false);

//   const handleProceedToComplete = () => {
//     const firstIncompleteSection = sections.find(
//       (section) =>
//         !section.completed &&
//         (section.key !== 'professional_detail' || user_type === '2')
//     );

//     if (firstIncompleteSection) {
//       setActiveModal(firstIncompleteSection.key);
//       setModalShow(true);
//     } else {
//       setIsAllCompleted(true);
//     }
//   };

//   useEffect(() => {
//     const allCompleted = sections.every(
//       (section) =>
//         section.completed ||
//         (section.key === 'professional_detail' && user_type !== '2')
//     );
//     setIsAllCompleted(allCompleted);
//   }, [user_updation_status]);

//   return (
//     <>
//       {['2', '5']?.includes(user_type) && researchProfileCount && (
//         <div
//           className={`${
//             registration_email_otp_status === '0' ? '' : 'cmb-24'
//           } d-flex justify-content-between cmb-11 gap-3`}
//         >
//           <div className="d-flex justify-content-between flex-column flex-grow-1 pt-1">
//             <div
//               className={`d-flex justify-content-between ${
//                 researchProfileCount < 100 ? '' : 'mb-2'
//               }`}
//             >
//               <div className="text-14-400-20 color-title-navy">
//                 Profile Status
//               </div>
//               <div className="text-14-400-20 color-title-navy">
//                 {researchProfileCount >= 100 ? 100 : researchProfileCount}%
//               </div>
//             </div>
//             <div className="progress" style={{ height: '6px' }}>
//               <div
//                 className="progress-bar bg-lime-green"
//                 role="progressbar"
//                 style={{ width: `${researchProfileCount}%` }}
//                 aria-valuenow={researchProfileCount}
//                 aria-valuemin="0"
//                 aria-valuemax="100"
//               ></div>
//             </div>
//           </div>

//           {registration_email_otp_status === '0' ? (
//             <Button
//               isSquare
//               text="Verify Email ID"
//               btnStyle="primary-dark"
//               className="cps-20 cpe-20 rounded h-35"
//               onClick={() => {
//                 // setShowEmailModel(true);
//                 // setModalShow(true);
//                 if (!checkEmailVerification(dispatch, navigate)) return;
//               }}
//             />
//           ) : (
//             <div
//               className="d-flex align-items-center gap-2 text-12-500-15"
//               style={{
//                 border: '1px solid #2148C0',
//                 borderRadius: '6px',
//                 padding: '8px 13px',
//                 background: '#EFF3FF',
//                 color: '#2148C0',
//               }}
//             >
//               <img src={icons?.RoundRightIcon} alt="round" />
//               Verified
//             </div>
//           )}
//         </div>
//       )}
//       {/* {registration_email_otp_status === "0" && ( */}
//       <div className="text-14-400-18 color-black-olive cmb-24">
//         Just {remainPercentage}% more to unlock all benefits and{' '}
//         <span
//           className="text-14-500-18 color-7cff pointer"
//           onClick={() => {
//             dispatch(setIsGlobalProfilePreviewPopup(true));
//           }}
//         >
//           Update Profile
//         </span>
//       </div>
//       {/* )} */}
//       <ul className="list-group list-group-flush cmb-22">
//         {sections
//           .filter(
//             (section) =>
//               section.key !== 'professional_detail' || user_type === '2'
//           )
//           .map(({ key, label, completed, action }, idx) => (
//             <li
//               key={key || idx}
//               className="list-group-item d-flex align-items-center border-0 px-0 py-2"
//             >
//               <div className="d-flex align-items-center justify-content-center gap-2">
//                 <img
//                   src={completed ? icons?.abstractDone : icons?.ClockPending}
//                   alt={completed ? 'Completed' : 'Pending'}
//                   height={24}
//                   width={24}
//                 />
//                 <p className="mb-0 text-14-400-18">{label}</p>
//               </div>

//               {!(
//                 key === 'verify_email' && email_verification_status === '1'
//               ) && (
//                 <button
//                   onClick={() => handleEditInfo(key)}
//                   className="btn text-decoration-none text-12-500-15 color-7cff shadow-none outline-0 border-0"
//                 >
//                   {action === 'Edit Details' && (
//                     <span className="me-1">
//                       <img src={icons.primarylightEdit} alt="" />
//                     </span>
//                   )}
//                   {action}
//                 </button>
//               )}
//             </li>
//           ))}
//       </ul>

//       {isShowButton && (
//         <div
//           className="d-flex gap-3 cmb-50"
//           style={{ maxWidth: '400px', width: '100%' }}
//         >
//           {!isAllCompleted && (
//             <div className="flex-fill">
//               <Button
//                 text="Proceed to Complete"
//                 btnStyle="primary-dark"
//                 className="h-35 cps-20 cpe-20"
//                 onClick={handleProceedToComplete}
//               />
//             </div>
//           )}
//           <div className="flex-fill">
//             <Button
//               text="Back"
//               btnStyle="primary-light-outline"
//               className="h-35 cps-20 cpe-20"
//               onClick={() => navigate(-1)}
//             />
//           </div>
//         </div>
//       )}
//       {modalShow && renderModal()}

//       {isGlobalProfilePreviewPopup && (
//         <GlobalProfilePreviewPopup
//           isLoading={isPageLoading}
//           fetchDetails={fetchDetails}
//         />
//       )}
//     </>
//   );
// };

// export default ProfileCompletion;
